// components/OrderDetails.tsx
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Order } from '../types';
import {
    createOrderShippingLabel,
    ensureRateReminder,
    getOrderById,
    requestOrderRefund,
    shipOrder,
} from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { ArrowLeft, Package, Truck, Calendar, Star } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import RatingPopup from './RatingPopup';
import { useQueryClient } from '@tanstack/react-query';
import { ValidationError } from '../admin/components/ValidationError';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';
import ShopSelect from './ShopSelect';
import { translateOrderStatus } from '../utils/orderStatusLabel';
import { formatPublicHandle } from '../utils/usernameRules';
import {
    createHoldCountdownAnchor,
    formatCountdown,
    remainingFromAnchor,
} from '../utils/reservationCountdown';

const OrderDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const queryClient = useQueryClient();
    const [order, setOrder] = useState<Order | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [showRating, setShowRating] = useState(false);
    const [showProblemForm, setShowProblemForm] = useState(false);
    const [problemReason, setProblemReason] = useState('');
    const [problemError, setProblemError] = useState<string | null>(null);
    const [problemSubmitting, setProblemSubmitting] = useState(false);
    const [carrier, setCarrier] = useState('bpost');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [trackingUrl, setTrackingUrl] = useState('');
    const [shipError, setShipError] = useState<string | null>(null);
    const [shipBusy, setShipBusy] = useState(false);

    const carrierOptions = [
        { value: 'bpost', label: 'Bpost' },
        { value: 'other', label: t('order.ship.carrierOther') },
    ];

    const loadOrder = () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        getOrderById(Number(id))
            .then((o) => {
                setOrder(o);
                const wantRate = searchParams.get('rate') === '1';
                const canRate =
                    !o.hasRated &&
                    (o.status === 'Delivered' || o.status === 'Completed');
                if (wantRate && canRate) {
                    setShowRating(true);
                }
            })
            .catch((err) => {
                console.error(err);
                setError(
                    tr('order.details.loadError', 'Failed to load order. Please try again later.')
                );
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const isLiveCheckoutTick =
        !!order?.isLiveCheckout && String(order?.status ?? '') === 'Pending';
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        if (!isLiveCheckoutTick) return;
        const timerId = window.setInterval(() => setNowMs(Date.now()), 1000);
        return () => window.clearInterval(timerId);
    }, [isLiveCheckoutTick]);

    // Must run every render (before early returns) — React #310 if useMemo only after load
    const holdAnchor = useMemo(
        () =>
            createHoldCountdownAnchor(
                order?.holdRemainingSeconds,
                order?.reservedUntil
            ),
        [order?.holdRemainingSeconds, order?.reservedUntil]
    );
    const holdLeftMs = remainingFromAnchor(holdAnchor, nowMs);
    const holdCountdown = formatCountdown(holdLeftMs);

    if (loading) return <LoadingSpinner />;
    if (error) return <div className="p-8 text-red-500">{error}</div>;
    if (!order)
        return (
            <p className="text-red-500 p-8">
                {tr('order.details.notFound', 'Order not found.')}
            </p>
        );

    const statusColors: Record<string, string> = {
        Pending: 'bg-yellow-100 text-yellow-800',
        Paid: 'bg-green-100 text-green-800',
        Shipped: 'bg-blue-100 text-blue-800',
        Delivered: 'bg-emerald-100 text-emerald-800',
        Completed: 'bg-emerald-100 text-emerald-800',
        Cancelled: 'bg-red-100 text-red-800',
        Refunded: 'bg-purple-100 text-purple-800',
    };

    const isBuyer = !order.isViewerSeller;
    const status = String(order.status ?? '');

    const canRate =
        isBuyer &&
        !order.hasRated &&
        (status === 'Delivered' || status === 'Completed');

    /**
     * Option B: formal problem report (ticket + Problem status) only after delivery.
     * Not an automatic money refund. Pre-delivery issues → seller / helpdesk.
     */
    const canReportProblem =
        isBuyer &&
        !order.isDisputed &&
        (status === 'Delivered' || status === 'Completed');

    const isLiveCheckout = !!order.isLiveCheckout && status === 'Pending';
    const isPendingCheckout = isBuyer && isLiveCheckout;
    const isPaymentNotCompleted = !!order.isPaymentNotCompleted;
    const isPreDeliveryPaid =
        isBuyer &&
        !order.isDisputed &&
        (status === 'Paid' || status === 'Processing');
    const isInTransit =
        isBuyer &&
        !order.isDisputed &&
        (status === 'Shipped' || status === 'InTransit');

    const handleShipManual = async (e: FormEvent) => {
        e.preventDefault();
        setShipError(null);
        if (!trackingNumber.trim()) {
            setShipError(t('order.ship.trackingRequired'));
            scrollToFirstErrorField({ trackingNumber: true });
            return;
        }
        setShipBusy(true);
        try {
            const updated = await shipOrder(order.id, {
                carrier,
                trackingNumber: trackingNumber.trim(),
                trackingUrl: trackingUrl.trim() || undefined,
            });
            setOrder(updated);
            await queryClient.invalidateQueries({ queryKey: ['mySales'] });
            await queryClient.invalidateQueries({ queryKey: ['myOrders'] });
        } catch (err: unknown) {
            let msg = t('order.ship.error');
            if (err && typeof err === 'object' && 'response' in err) {
                const data = (err as { response?: { data?: { message?: string } } }).response?.data;
                if (data?.message) msg = data.message;
            }
            setShipError(msg);
        } finally {
            setShipBusy(false);
        }
    };

    const handleCreateLabel = async () => {
        setShipError(null);
        setShipBusy(true);
        try {
            const updated = await createOrderShippingLabel(order.id);
            setOrder(updated);
            await queryClient.invalidateQueries({ queryKey: ['mySales'] });
        } catch (err: unknown) {
            let msg = t('order.ship.labelError');
            if (err && typeof err === 'object' && 'response' in err) {
                const data = (err as { response?: { data?: { message?: string } } }).response?.data;
                if (data?.message) msg = data.message;
            }
            setShipError(msg);
        } finally {
            setShipBusy(false);
        }
    };

    const handleRatingClosed = () => {
        setShowRating(false);
        if (searchParams.get('rate')) {
            searchParams.delete('rate');
            setSearchParams(searchParams, { replace: true });
        }
    };

    const handleReportProblem = async (e: FormEvent) => {
        e.preventDefault();
        setProblemError(null);
        if (!problemReason.trim()) {
            setProblemError(
                tr('order.issue.reasonRequired', 'Please describe what went wrong with this order.')
            );
            scrollToFirstErrorField({ problemReason: true });
            return;
        }
        setProblemSubmitting(true);
        try {
            // API still named refund; creates ticket + Problem status (no automatic payment)
            const updated = await requestOrderRefund(order.id, problemReason.trim());
            setOrder(updated);
            setShowProblemForm(false);
            setProblemReason('');
            await queryClient.invalidateQueries({ queryKey: ['myOrders'] });
        } catch (err: unknown) {
            let msg = tr('order.issue.error', 'Could not submit problem report.');
            if (err && typeof err === 'object' && 'response' in err) {
                const data = (err as { response?: { data?: { message?: string } } }).response?.data;
                if (data?.message) msg = data.message;
            }
            setProblemError(msg);
            scrollToFirstErrorField({ problemReason: true });
        } finally {
            setProblemSubmitting(false);
        }
    };

    return (
        <div className="mx-auto max-w-4xl py-2 sm:p-6">
            <Link
                to={order.isViewerSeller ? '/my-account/sales' : '/my-account/orders'}
                className="mb-6 flex min-h-11 items-center gap-2 text-[var(--accent-color)] hover:underline"
            >
                <ArrowLeft className="w-4 h-4" /> {tr('account.hub.back', 'Back to My Account')}
            </Link>

            <div className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_70%)] bg-[color-mix(in_srgb,var(--bg-color),white_15%)] p-4 shadow-md sm:p-8">
                <div className="mb-6 flex flex-col gap-3 sm:mb-8 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                        <h1 className="text-2xl font-bold text-[var(--text-color)] sm:text-3xl">
                            {(isLiveCheckout
                                ? tr('order.details.title.checkout', 'Checkout #{id}')
                                : tr('order.details.title', 'Order #{id}')
                            ).replace('{id}', String(order.id))}
                        </h1>
                        <p className="text-[color-mix(in_srgb,var(--text-color),transparent_40%)] mt-1">
                            {(isLiveCheckout
                                ? tr(
                                      'order.details.checkoutStartedOn',
                                      'Checkout started on {date}'
                                  )
                                : isPaymentNotCompleted
                                  ? tr(
                                        'order.details.checkoutStartedOn',
                                        'Checkout started on {date}'
                                    )
                                  : tr('order.details.placedOn', 'Placed on {date}')
                            ).replace(
                                '{date}',
                                new Date(order.orderDate || Date.now()).toLocaleDateString()
                            )}
                        </p>
                    </div>
                    <div className="flex flex-row flex-wrap items-center gap-2 sm:flex-col sm:items-end">
                        <div
                            className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                                isPaymentNotCompleted
                                    ? 'bg-gray-200 text-gray-700'
                                    : statusColors[order.status] || 'bg-gray-100'
                            }`}
                        >
                            {translateOrderStatus(tr, String(order.status), {
                                isViewerSeller: !!order.isViewerSeller,
                                isLiveCheckout: order.isLiveCheckout,
                                isPaymentNotCompleted: order.isPaymentNotCompleted,
                                paymentStatus: order.paymentStatus,
                            })}
                        </div>
                        {order.isDisputed && (
                            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-900">
                                {tr('order.issue.open', 'Problem reported')}
                            </span>
                        )}
                    </div>
                </div>

                {isLiveCheckout && (
                    <div className="mb-6 rounded-xl border border-amber-300/80 bg-amber-50/90 p-4 text-sm text-amber-950">
                        {isBuyer ? (
                            <>
                                <p className="font-semibold">
                                    {tr(
                                        'order.checkout.hold.buyer.title',
                                        'You’re holding this item — complete payment to buy it.'
                                    )}
                                </p>
                                {holdLeftMs > 0 && (
                                    <p className="mt-1">
                                        {tr('order.checkout.payWithin', 'Pay within')}{' '}
                                        <strong>{holdCountdown}</strong>
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className="font-semibold">
                                {tr(
                                    'order.checkout.hold.seller.title',
                                    'A buyer is checking out — reserved until they pay or the timer ends.'
                                )}
                                {holdLeftMs > 0 ? ` (${holdCountdown})` : ''}
                            </p>
                        )}
                    </div>
                )}

                {isPaymentNotCompleted && (
                    <div className="mb-6 rounded-xl border border-gray-300 bg-gray-50 p-4 text-sm text-gray-800">
                        <p className="font-semibold">
                            {tr('order.status.PaymentNotCompleted', 'Payment not completed')}
                        </p>
                        <p className="mt-1">
                            {tr(
                                'order.checkout.paymentNotCompleted.detail',
                                'Payment was not completed in time. The item was released.'
                            )}
                        </p>
                        {order.productId && order.product?.status !== 'Sold' && (
                            <Link
                                to={`/products/${order.productId}`}
                                className="mt-2 inline-flex font-semibold text-[var(--accent-color)] underline"
                            >
                                {tr('order.checkout.viewListing', 'View listing')}
                            </Link>
                        )}
                    </div>
                )}

                <div className="grid gap-6 md:grid-cols-2 md:gap-8">
                    <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2 text-[var(--text-color)]">
                            <Package className="w-5 h-5" />{' '}
                            {tr('order.details.product', 'Product')}
                        </h3>
                        <div className="bg-[color-mix(in_srgb,var(--bg-color),white_25%)] rounded-xl p-4 flex gap-4">
                            <img
                                src={order.product?.imageUrls?.[0] || '/placeholder.jpg'}
                                alt=""
                                className="w-24 h-24 object-cover rounded-lg"
                            />
                            <div>
                                <p className="font-semibold text-[var(--text-color)]">
                                    {order.product?.title}
                                </p>
                                <p className="text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                    {tr('order.details.seller', 'Seller')}:{' '}
                                    {formatPublicHandle(order.product?.sellerFirstName)}
                                </p>
                                {order.product?.sellerId && (
                                    <Link
                                        to={`/users/${order.product.sellerId}`}
                                        className="text-sm text-[var(--accent-color)] underline"
                                    >
                                        {tr('profile.view', 'View profile')}
                                    </Link>
                                )}
                                <p className="text-xl font-bold mt-2 text-[var(--text-color)]">
                                    {'\u20AC'}
                                    {order.total.toFixed(2)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2 text-[var(--text-color)]">
                            <Truck className="w-5 h-5" />{' '}
                            {tr('order.details.shipping', 'Shipping')}
                        </h3>
                        <div className="bg-[color-mix(in_srgb,var(--bg-color),white_25%)] rounded-xl p-4 space-y-2 text-sm text-[var(--text-color)]">
                            {(() => {
                                const hasAddress = [
                                    order.shippingStreetAddress,
                                    order.shippingCity,
                                    order.shippingPostalCode,
                                    order.shippingCountry,
                                ].some((v) => !!String(v ?? '').trim());

                                if (!hasAddress && (isLiveCheckout || status === 'Pending')) {
                                    return (
                                        <p className="text-[color-mix(in_srgb,var(--text-color),transparent_25%)]">
                                            {tr(
                                                'order.details.address.afterCheckout',
                                                'Shipping address is entered during secure payment on Shopify. It will show here after you complete checkout.'
                                            )}
                                        </p>
                                    );
                                }
                                if (!hasAddress) {
                                    return (
                                        <p className="text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                                            {tr(
                                                'order.details.address.none',
                                                'No shipping address on this order.'
                                            )}
                                        </p>
                                    );
                                }
                                return (
                                    <>
                                        <p>
                                            <strong>
                                                {tr('order.details.address', 'Address')}:
                                            </strong>{' '}
                                            {[
                                                order.shippingStreetAddress,
                                                order.shippingCity,
                                                order.shippingPostalCode,
                                            ]
                                                .filter((v) => !!String(v ?? '').trim())
                                                .join(', ')}
                                        </p>
                                        {order.shippingCountry && (
                                            <p>
                                                <strong>
                                                    {tr('order.details.country', 'Country')}:
                                                </strong>{' '}
                                                {order.shippingCountry}
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                            {order.trackingNumber && (
                                <p className="mt-3">
                                    <strong>{tr('order.ship.tracking', 'Tracking')}:</strong>{' '}
                                    {order.trackingUrl ? (
                                        <a
                                            href={order.trackingUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-[var(--accent-color)] underline"
                                        >
                                            {order.trackingNumber} ({order.trackingCompany})
                                        </a>
                                    ) : (
                                        <span>
                                            {order.trackingNumber} ({order.trackingCompany})
                                        </span>
                                    )}
                                </p>
                            )}
                            {order.labelPdfUrl && (
                                <p>
                                    <a
                                        href={order.labelPdfUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="font-medium text-[var(--accent-color)] underline"
                                    >
                                        {tr('order.ship.labelPdf', 'Download shipping label')}
                                    </a>
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Seller: label-first (Vinted-style) — manual tracking is secondary */}
                {order.isViewerSeller && order.canShip && (
                    <div className="mt-8 rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] p-4 sm:p-6">
                        <h3 className="mb-2 flex items-center gap-2 text-lg font-semibold text-[var(--text-color)]">
                            <Truck className="h-5 w-5 shrink-0" aria-hidden />
                            {t('order.ship.title')}
                        </h3>
                        <p className="mb-4 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                            {order.labelsAvailable
                                ? t('order.ship.helpLabelFirst')
                                : t('order.ship.help')}
                        </p>

                        {order.labelsAvailable ? (
                            <>
                                <ol className="mb-4 list-decimal space-y-1 pl-5 text-sm text-[var(--text-color)]">
                                    <li>{t('order.ship.step1')}</li>
                                    <li>{t('order.ship.step2')}</li>
                                    <li>{t('order.ship.step3')}</li>
                                </ol>
                                <button
                                    type="button"
                                    disabled={shipBusy}
                                    onClick={handleCreateLabel}
                                    className="flex min-h-12 w-full items-center justify-center rounded-xl bg-[var(--accent-color)] px-4 py-3.5 text-base font-semibold text-[var(--text-color)] shadow-sm disabled:opacity-60"
                                >
                                    {shipBusy ? t('state.loading') : t('order.ship.createLabel')}
                                </button>
                                <p className="mt-2 text-center text-xs text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                                    {t('order.ship.labelPrimaryHint')}
                                </p>
                                <details className="mt-6 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] p-3">
                                    <summary className="cursor-pointer text-sm font-medium text-[var(--text-color)]">
                                        {t('order.ship.manualAdvanced')}
                                    </summary>
                                    <form onSubmit={handleShipManual} className="mt-4 space-y-3">
                                        <div>
                                            <label
                                                className="mb-1.5 block text-sm font-medium text-[var(--text-color)]"
                                                htmlFor="carrier"
                                            >
                                                {t('order.ship.carrier')}
                                            </label>
                                            <ShopSelect
                                                id="carrier"
                                                value={carrier}
                                                onChange={setCarrier}
                                                options={carrierOptions}
                                                ariaLabel={t('order.ship.carrier')}
                                                disabled={shipBusy}
                                            />
                                        </div>
                                        <div data-error-field="trackingNumber">
                                            <label
                                                className="mb-1.5 block text-sm font-medium text-[var(--text-color)]"
                                                htmlFor="trackingNumber"
                                            >
                                                {t('order.ship.trackingNumber')}
                                            </label>
                                            <input
                                                id="trackingNumber"
                                                value={trackingNumber}
                                                onChange={(e) => setTrackingNumber(e.target.value)}
                                                className="w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] p-3 text-base text-[var(--text-color)] outline-none focus:border-[var(--highlight-color,var(--accent-color))] focus:ring-2 focus:ring-[var(--highlight-color,var(--accent-color))]/30"
                                                autoComplete="off"
                                                inputMode="text"
                                            />
                                        </div>
                                        <div>
                                            <label
                                                className="mb-1.5 block text-sm font-medium text-[var(--text-color)]"
                                                htmlFor="trackingUrl"
                                            >
                                                {t('order.ship.trackingUrl')}
                                            </label>
                                            <input
                                                id="trackingUrl"
                                                value={trackingUrl}
                                                onChange={(e) => setTrackingUrl(e.target.value)}
                                                className="w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] p-3 text-base text-[var(--text-color)] placeholder:text-[color-mix(in_srgb,var(--text-color),transparent_50%)] outline-none focus:border-[var(--highlight-color,var(--accent-color))] focus:ring-2 focus:ring-[var(--highlight-color,var(--accent-color))]/30"
                                                placeholder="https://"
                                                inputMode="url"
                                                autoComplete="off"
                                            />
                                        </div>
                                        <ValidationError error={shipError ?? undefined} />
                                        <button
                                            type="submit"
                                            disabled={shipBusy}
                                            className="flex min-h-11 w-full items-center justify-center rounded-xl border border-[var(--accent-color)] px-4 py-3 text-sm font-semibold text-[var(--text-color)] disabled:opacity-60"
                                        >
                                            {shipBusy ? t('state.loading') : t('order.ship.submit')}
                                        </button>
                                    </form>
                                </details>
                            </>
                        ) : (
                            <>
                                <p className="mb-3 rounded-xl border border-amber-200/80 bg-amber-50/90 p-3 text-sm text-amber-950">
                                    {t('order.ship.labelsNotConfigured')}
                                </p>
                                <form onSubmit={handleShipManual} className="space-y-3">
                                    <div>
                                        <label
                                            className="mb-1.5 block text-sm font-medium text-[var(--text-color)]"
                                            htmlFor="carrier-fallback"
                                        >
                                            {t('order.ship.carrier')}
                                        </label>
                                        <ShopSelect
                                            id="carrier-fallback"
                                            value={carrier}
                                            onChange={setCarrier}
                                            options={carrierOptions}
                                            ariaLabel={t('order.ship.carrier')}
                                            disabled={shipBusy}
                                        />
                                    </div>
                                    <div data-error-field="trackingNumber">
                                        <label
                                            className="mb-1.5 block text-sm font-medium text-[var(--text-color)]"
                                            htmlFor="trackingNumber"
                                        >
                                            {t('order.ship.trackingNumber')}
                                        </label>
                                        <input
                                            id="trackingNumber"
                                            value={trackingNumber}
                                            onChange={(e) => setTrackingNumber(e.target.value)}
                                            className="w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] p-3 text-base text-[var(--text-color)] outline-none focus:border-[var(--highlight-color,var(--accent-color))] focus:ring-2 focus:ring-[var(--highlight-color,var(--accent-color))]/30"
                                            autoComplete="off"
                                            inputMode="text"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            className="mb-1.5 block text-sm font-medium text-[var(--text-color)]"
                                            htmlFor="trackingUrl"
                                        >
                                            {t('order.ship.trackingUrl')}
                                        </label>
                                        <input
                                            id="trackingUrl"
                                            value={trackingUrl}
                                            onChange={(e) => setTrackingUrl(e.target.value)}
                                            className="w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] p-3 text-base text-[var(--text-color)] placeholder:text-[color-mix(in_srgb,var(--text-color),transparent_50%)] outline-none focus:border-[var(--highlight-color,var(--accent-color))] focus:ring-2 focus:ring-[var(--highlight-color,var(--accent-color))]/30"
                                            placeholder="https://"
                                            inputMode="url"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <ValidationError error={shipError ?? undefined} />
                                    <button
                                        type="submit"
                                        disabled={shipBusy}
                                        className="flex min-h-11 w-full items-center justify-center rounded-xl border border-[var(--accent-color)] px-4 py-3 text-sm font-semibold text-[var(--text-color)] disabled:opacity-60"
                                    >
                                        {shipBusy ? t('state.loading') : t('order.ship.submit')}
                                    </button>
                                </form>
                            </>
                        )}
                        {shipError && order.labelsAvailable && (
                            <div className="mt-3">
                                <ValidationError error={shipError} />
                            </div>
                        )}
                    </div>
                )}

                {/* After label: lead seller to print / drop-off */}
                {order.isViewerSeller && order.labelPdfUrl && (
                    <div className="mt-6 rounded-2xl border-2 border-[var(--accent-color)] bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)] p-4 sm:p-6">
                        <h3 className="mb-2 text-lg font-bold text-[var(--text-color)]">
                            {t('order.ship.labelReadyTitle')}
                        </h3>
                        <p className="mb-4 text-sm text-[var(--text-color)]">
                            {t('order.ship.labelReadyBody')}
                        </p>
                        <a
                            href={order.labelPdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex min-h-12 w-full items-center justify-center rounded-xl bg-[var(--accent-color)] px-4 py-3.5 text-base font-semibold text-[var(--text-color)] shadow-sm sm:w-auto"
                        >
                            {t('order.ship.labelPdf')}
                        </a>
                        {order.trackingNumber && (
                            <p className="mt-3 text-sm text-[var(--text-color)]">
                                {t('order.ship.tracking')}:{' '}
                                <strong>{order.trackingNumber}</strong>
                                {order.trackingUrl && (
                                    <>
                                        {' · '}
                                        <a
                                            href={order.trackingUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="underline"
                                        >
                                            {t('account.myorders.track')}
                                        </a>
                                    </>
                                )}
                            </p>
                        )}
                    </div>
                )}

                <div className="mt-8">
                    <h3 className="font-semibold mb-4 flex items-center gap-2 text-[var(--text-color)]">
                        <Calendar className="w-5 h-5" />{' '}
                        {tr('order.details.timeline', 'Order timeline')}
                    </h3>
                    <div className="space-y-4 border-l-2 border-[color-mix(in_srgb,var(--accent-color),transparent_60%)] pl-6">
                        {(() => {
                            type StepState = 'done' | 'current' | 'todo';
                            type Step = {
                                key: string;
                                title: string;
                                detail?: string;
                                state: StepState;
                            };

                            const paidLike =
                                status === 'Paid' ||
                                status === 'Processing' ||
                                status === 'Shipped' ||
                                status === 'InTransit' ||
                                status === 'Delivered' ||
                                status === 'Completed' ||
                                status === 'Problem';

                            const steps: Step[] = [];

                            // 1) Checkout started (always — hold or real order)
                            steps.push({
                                key: 'checkout',
                                title: tr(
                                    'order.details.timeline.checkoutStarted',
                                    'Checkout started'
                                ),
                                detail: order.orderDate
                                    ? new Date(order.orderDate).toLocaleString()
                                    : undefined,
                                state: 'done',
                            });

                            if (isLiveCheckout || (status === 'Pending' && !isPaymentNotCompleted)) {
                                // Live unpaid hold
                                steps.push({
                                    key: 'reserved',
                                    title: tr(
                                        'order.details.timeline.reservedAwaiting',
                                        'Item temporarily reserved - awaiting payment'
                                    ),
                                    detail:
                                        holdLeftMs > 0
                                            ? `${tr('order.checkout.payWithin', 'Pay within')} ${holdCountdown}`
                                            : undefined,
                                    state: 'current',
                                });
                                steps.push({
                                    key: 'placed',
                                    title: tr(
                                        'order.details.timeline.orderPlaced',
                                        'Order placed'
                                    ),
                                    detail: tr(
                                        'order.details.timeline.afterPayment',
                                        'After payment'
                                    ),
                                    state: 'todo',
                                });
                                steps.push({
                                    key: 'paid',
                                    title: tr(
                                        'order.details.paymentReceived',
                                        'Payment received'
                                    ),
                                    state: 'todo',
                                });
                            } else if (isPaymentNotCompleted) {
                                steps.push({
                                    key: 'notPaid',
                                    title: tr(
                                        'order.status.PaymentNotCompleted',
                                        'Payment not completed'
                                    ),
                                    detail: tr(
                                        'order.checkout.paymentNotCompleted.detail',
                                        'Payment was not completed in time. The item was released.'
                                    ),
                                    state: 'done',
                                });
                            } else {
                                // Paid-like path: order placed then payment (both done once paid)
                                if (paidLike) {
                                    steps.push({
                                        key: 'placed',
                                        title: tr(
                                            'order.details.timeline.orderPlaced',
                                            'Order placed'
                                        ),
                                        state: 'done',
                                    });
                                    steps.push({
                                        key: 'paid',
                                        title: tr(
                                            'order.details.paymentReceived',
                                            'Payment received'
                                        ),
                                        state: 'done',
                                    });
                                }
                                if (order.shippedDate || status === 'Shipped' || status === 'InTransit') {
                                    steps.push({
                                        key: 'shipped',
                                        title: tr('order.details.shipped', 'Shipped'),
                                        detail: order.shippedDate
                                            ? new Date(order.shippedDate).toLocaleString()
                                            : undefined,
                                        state: 'done',
                                    });
                                }
                                if (status === 'Delivered' || status === 'Completed') {
                                    steps.push({
                                        key: 'delivered',
                                        title: tr('order.details.delivered', 'Delivered'),
                                        state: 'done',
                                    });
                                }
                                if (status === 'Problem') {
                                    steps.push({
                                        key: 'problem',
                                        title: tr('order.issue.open', 'Problem reported'),
                                        state: 'current',
                                    });
                                }
                                if (status === 'Cancelled' && !isPaymentNotCompleted) {
                                    steps.push({
                                        key: 'cancelled',
                                        title: tr('order.status.Cancelled', 'Cancelled'),
                                        state: 'done',
                                    });
                                }
                                if (status === 'Refunded') {
                                    steps.push({
                                        key: 'refunded',
                                        title: tr('order.status.Refunded', 'Refunded'),
                                        state: 'done',
                                    });
                                }
                            }

                            const dotClass = (s: StepState) =>
                                s === 'done'
                                    ? 'bg-green-500'
                                    : s === 'current'
                                      ? 'bg-amber-500 ring-2 ring-amber-200'
                                      : 'bg-gray-300';

                            const titleClass = (s: StepState) =>
                                s === 'todo'
                                    ? 'font-medium text-[color-mix(in_srgb,var(--text-color),transparent_45%)]'
                                    : 'font-medium text-[var(--text-color)]';

                            return steps.map((step) => (
                                <div key={step.key} className="flex gap-3">
                                    <div
                                        className={`mt-1.5 h-3 w-3 shrink-0 rounded-full ${dotClass(step.state)}`}
                                    />
                                    <div className="min-w-0">
                                        <p className={titleClass(step.state)}>{step.title}</p>
                                        {step.detail && (
                                            <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                                                {step.detail}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ));
                        })()}
                    </div>
                </div>

                <div className="mt-8 pt-6 border-t border-[color-mix(in_srgb,var(--accent-color),transparent_70%)] flex flex-wrap justify-between items-center gap-4">
                    <div>
                        <p className="text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                            {tr('order.details.total', 'Total')}
                        </p>
                        <p className="text-3xl font-bold text-[var(--text-color)]">
                            {'\u20AC'}
                            {order.total.toFixed(2)}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {canRate && (
                            <button
                                type="button"
                                onClick={() => setShowRating(true)}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[var(--accent-color)] text-white font-semibold hover:opacity-90"
                            >
                                <Star className="w-4 h-4" />
                                {tr('rating.cta', 'Rate seller')}
                            </button>
                        )}
                        {order.hasRated && (
                            <span className="inline-flex items-center gap-1 px-4 py-2 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                                <Star className="w-4 h-4 fill-[var(--accent-color)] text-[var(--accent-color)]" />
                                {tr('rating.done', 'Rated')}
                            </span>
                        )}
                        {/* Post-delivery: formal problem report (Option B) */}
                        {canReportProblem && (
                            <button
                                type="button"
                                onClick={() => setShowProblemForm((v) => !v)}
                                className="px-4 py-3 rounded-xl border border-amber-400/70 bg-amber-50 text-sm font-medium text-amber-950 hover:bg-amber-100"
                            >
                                {tr('order.issue.cta', 'Report a problem')}
                            </button>
                        )}

                        {/* Live Pending: Finish payment primary; helpdesk secondary */}
                        {isPendingCheckout && (
                            <>
                                <Link
                                    to={`/checkout/${order.productId}`}
                                    className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-[var(--accent-color)] text-white font-semibold hover:opacity-90"
                                >
                                    {tr('order.checkout.finishPayment', 'Finish payment')}
                                </Link>
                                <Link
                                    to={`/helpdesk/order-issue?orderId=${order.id}`}
                                    className="px-4 py-3 rounded-xl border border-red-300 bg-red-50 text-sm font-medium text-red-900 hover:bg-red-100"
                                >
                                    {tr('order.issue.pending.cta', 'Need help with this checkout?')}
                                </Link>
                            </>
                        )}

                        {/* Paid / processing (not shipped): helpdesk only — no chat; profile is on product card */}
                        {isPreDeliveryPaid && (
                            <Link
                                to={`/helpdesk/order-issue?orderId=${order.id}`}
                                className="px-4 py-3 rounded-xl border border-amber-300 bg-amber-50 text-sm font-medium text-amber-950 hover:bg-amber-100"
                            >
                                {tr('order.issue.preShip.helpdesk', 'Get help before delivery')}
                            </Link>
                        )}

                        {/* Shipped / in transit: delivery issues via helpdesk (not item return) */}
                        {isInTransit && (
                            <Link
                                to={`/helpdesk/order-issue?orderId=${order.id}`}
                                className="px-4 py-3 rounded-xl border border-amber-300 bg-amber-50 text-sm font-medium text-amber-950 hover:bg-amber-100"
                            >
                                {tr('order.issue.inTransit.cta', 'Report a delivery issue')}
                            </Link>
                        )}
                    </div>
                </div>

                {isPreDeliveryPaid && (
                    <p className="mt-4 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] px-4 py-3 text-sm text-[var(--text-color)]">
                        {tr(
                            'order.issue.preShip.hint',
                            'You can report a problem with the item after it is delivered. Before delivery, contact the helpdesk if you need support.'
                        )}
                    </p>
                )}

                {isInTransit && (
                    <p className="mt-4 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] px-4 py-3 text-sm text-[var(--text-color)]">
                        {tr(
                            'order.issue.inTransit.hint',
                            'Package on the way. Use tracking if available. For delays or non-arrival, report a delivery issue — item returns open after delivery.'
                        )}
                    </p>
                )}

                {showProblemForm && canReportProblem && (
                    <form
                        onSubmit={handleReportProblem}
                        className="mt-6 space-y-3 rounded-xl border border-amber-200 bg-amber-50/60 p-5"
                    >
                        <h3 className="font-semibold text-[var(--text-color)]">
                            {tr('order.issue.title', 'Report a problem')}
                        </h3>
                        <p className="text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                            {tr(
                                'order.issue.help',
                                'For issues after delivery (damaged, not as described, wrong item). We review with the seller. This does not refund payment automatically.'
                            )}
                        </p>
                        <div data-error-field="problemReason">
                            <textarea
                                name="problemReason"
                                value={problemReason}
                                onChange={(e) => setProblemReason(e.target.value)}
                                rows={4}
                                maxLength={2000}
                                required
                                placeholder={tr(
                                    'order.issue.placeholder',
                                    'e.g. Item not as described, damaged, missing parts…'
                                )}
                                className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm text-[var(--text-color)] ${
                                    problemError ? 'border-red-300' : 'border-amber-200'
                                }`}
                            />
                            <ValidationError error={problemError ?? undefined} />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="submit"
                                disabled={problemSubmitting}
                                className="rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                            >
                                {problemSubmitting
                                    ? '…'
                                    : tr('order.issue.submit', 'Submit problem report')}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowProblemForm(false);
                                    setProblemError(null);
                                }}
                                className="rounded-xl border border-amber-300 px-4 py-2.5 text-sm text-amber-950"
                            >
                                {tr('common.cancel', 'Cancel')}
                            </button>
                        </div>
                    </form>
                )}

                {order.isDisputed && (
                    <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                        {tr(
                            'order.issue.pendingHelp',
                            'Your problem report is with our team. Payment is not refunded automatically.'
                        )}
                    </p>
                )}
            </div>

            <RatingPopup
                isOpen={showRating}
                onClose={handleRatingClosed}
                orderId={order.id}
                productTitle={order.product?.title}
                productImageUrl={order.product?.imageUrls?.[0]}
                onSkipOrClose={async () => {
                    try {
                        await ensureRateReminder(order.id);
                        await queryClient.invalidateQueries({ queryKey: ['notifications'] });
                    } catch (e) {
                        console.error(e);
                    }
                }}
                onSubmitted={() => {
                    setOrder((prev) => (prev ? { ...prev, hasRated: true } : prev));
                    queryClient.invalidateQueries({ queryKey: ['pendingRatings'] });
                    queryClient.invalidateQueries({ queryKey: ['notifications'] });
                }}
            />
        </div>
    );
};

export default OrderDetails;
